import { AsyncPipe } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { ActivityStatus } from "~app/common/models/constants";
import { AnvandareService } from "~app/common/services/anvandare.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { FeatureService } from "~app/common/services/feature.service";
import { ActivityViewModel } from "~app/pages/start/aktiviteter/ActivityViewModel";
import { BusySpinnerComponent } from "../busy/busy-spinner/busy-spinner.component";
import { IconComponent } from "../icon/icon.component";
import { SpanComponent } from "../span/span.component";
import { EventhubStatusComponent } from "./eventhub-status/eventhub-status.component";
import { FooterActivityPopoverComponent } from "./footer-activity-popover/footer-activity-popover.component";
import { FooterWorkMessageService } from "./footerWorkMessage.service";

@Component({
    selector: "rask-footer",
    styleUrls: ["./footer.component.scss"],
    templateUrl: "./footer.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RouterLink,
        BusySpinnerComponent,
        IconComponent,
        SpanComponent,
        FooterActivityPopoverComponent,
        AsyncPipe,
        EventhubStatusComponent
    ]
})
export class FooterComponent implements OnDestroy, OnInit {
    activeAktiviteter: ActivityViewModel[];
    aktiviteter: ActivityViewModel[];
    completedAktiviteter: ActivityViewModel[];
    currentWorkFlat$ = new BehaviorSubject(null);
    errorAktiviteter: ActivityViewModel[];

    protected evehubStatusFeatureActive$: Observable<boolean>;

    private readonly anvandareService = inject(AnvandareService);
    private readonly eventService = inject(EventService);
    private readonly featureService = inject(FeatureService);
    private readonly footerService = inject(FooterWorkMessageService);
    private readonly pulse$ = new Subject<void>();
    private readonly unsubscribe$ = new Subject<void>();

    dismissOkActivities() {
        this.anvandareService.dismissActivities(this.completedAktiviteter.map(x => x.id), true);
    }

    getErrorText(): string {
        return `${this.errorAktiviteter?.length} fel`;
    }

    getOkText(): string {
        return this.errorAktiviteter?.length === 0 && this.activeAktiviteter?.length === 0
            ? "Alla aktiviteter utförda" : `${this.completedAktiviteter?.length} Aktiviteter utförda`;
    }

    getPendingText(): string {
        return `${this.activeAktiviteter?.length} aktivitet pågår`;
    }

    getStatusIcon(status: string) {
        return this.anvandareService.getActivityStatusIcon(status);
    }

    ngOnDestroy() {
        this.footerService.disconnect();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.evehubStatusFeatureActive$ = this.featureService.getFeatureActive("EventhubStatus");

        this.footerService.connect();
        this.footerService.currentWorkFlat$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(x => this.currentWorkFlat$.next(x));

        this.eventService.subscribeAllArbetsorderSaga().pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(x => {
            this.pulse$.next();
        });

        this.pulse$
            .pipe(debounceTime(100))
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.anvandareService.getAktivitetshistorik();
            });
        this.pulse$.next();

        this.anvandareService.aktiviteter$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(res => {
            this.aktiviteter = res.activities.map(x => new ActivityViewModel(x));
            this.activeAktiviteter = res.activities.filter(x => x.status === ActivityStatus.Pagaende).map(x => new ActivityViewModel(x));
            this.errorAktiviteter = res.activities.filter(x => x.status === ActivityStatus.Fel).map(x => new ActivityViewModel(x));
            this.completedAktiviteter = res.activities.filter(x => x.status === ActivityStatus.Ok).map(x => new ActivityViewModel(x));
        });
    }

    showError(): boolean {
        return this.errorAktiviteter?.length > 0;
    }

    showOk(): boolean {
        return this.completedAktiviteter?.length > 0;
    }

    showPending(): boolean {
        return this.activeAktiviteter?.length > 0;
    }
}
